import React, { useEffect, useState } from 'react';
import iconWarning from '@/assets/app/images/icon/icon-warning.png';
import AppButton from '@/shared/components/ui/new/AppButton';
import useAppAuthStore, { getAuthState } from '@/shared/store/UseAppAuthStore';
import { vAppAjax } from '@/shared/utils/CommonUtil';
import logger from '@/shared/utils/Logger';

interface UpdateNicknameSheetProps {
  onCancel: () => void;
}

export const UpdateNicknameSheet: React.FC<UpdateNicknameSheetProps> = ({ onCancel }) => {
  const { member, updateMember } = getAuthState();
  const [nickname, setNickname] = useState(member?.nickname);
  const [status, setStatus] = useState({ type: '', message: '' }); // 안내 메시지 상태

  // 닉네임이 변경되면 중복 체크 상태 리셋
  useEffect(() => {
    setStatus({ type: '', message: '' });
  }, [nickname]);

  useEffect(() => {
    checkNicknameStatus();
  }, []);

  const validateNickname = (name: string) => {
    const regex = /^[가-힣a-zA-Z0-9]{2,10}$/;
    return regex.test(name);
  };

  const canChangeNickname = (lastUpdateDate: string | undefined) => {
    // 1. 날짜가 없으면 한 번도 변경 안 한 것이므로 가능
    if (!lastUpdateDate) return true;

    const lastDate = new Date(lastUpdateDate);
    const now = new Date();

    // 2. 연도와 월이 모두 현재와 같다면 이번 달에 변경한 것임
    const isSameMonth =
      lastDate.getFullYear() === now.getFullYear() && lastDate.getMonth() === now.getMonth();

    return !isSameMonth;
  };

  const checkNicknameStatus = () => {
    if (!canChangeNickname(member?.lastNicknameUpdateDate)) {
      // 다음 달 1일 구하기
      const now = new Date();
      const nextAvailableDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);

      const formattedDate = `${nextAvailableDate.getMonth() + 1}월 1일`;

      setStatus({
        type: 'disabled',
        message: `이번 달 변경 횟수를 모두 사용하셨습니다.<br/> ${formattedDate}부터 다시 변경 가능합니다.`,
      });
    }
  };

  const updateNickname = async () => {
    if (nickname && !validateNickname(nickname)) {
      setStatus({ type: 'error', message: '2~10자 한글, 영문, 숫자만 가능합니다.' });
      return;
    }
    if (nickname === member?.nickname) {
      setStatus({ type: 'error', message: '현재와 동일한 닉네임입니다.' });
      return;
    }

    await vAppAjax(
      'get',
      'members/check-nickname',
      { nickname: nickname },
      (response: AxiosResponse<CommonResult>) => {
        if (response.data.code === 200) {
          const resultData = response.data.result;
          if (!resultData.available) {
            setStatus({ type: 'error', message: '이미 사용 중인 닉네임입니다.' });
            return;
          }

          vAppAjax(
            'post',
            'members/update-nickname',
            { nickname: nickname },
            (response: AxiosResponse<CommonResult>) => {
              if (response.data.code === 200) {
                updateMember({
                  nickname: nickname,
                  lastNicknameUpdateDate: new Date().toISOString(),
                });
                onCancel();
              } else {
                setStatus({ type: 'error', message: response.data.message });
                logger.error(response.data.message);
              }
            },
            (error: any) => {
              logger.error(error);
            },
          );
        } else {
          logger.error(response.data.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  return (
    <div className="p-2 flex flex-col items-center text-center">
      <div className="space-y-2 w-full">
        <div className="">
          <input
            type="text"
            value={nickname}
            onChange={e => setNickname(e.target.value)}
            placeholder="새 닉네임을 작성하세요"
            className="w-full rounded-xl p-4 outline-none focus:border-[#00A9CE]"
            style={{ border: '1px solid #8C8989', fontSize: 18, borderRadius: 40 }}
            disabled={status.type === 'disabled'}
          />

          {/* 상태 메시지 영역 */}
          {status.type === 'error' || status.type === 'disabled' ? (
            <p
              className={`text-xs pt-2 mb-10 float-left text-left text-red-500`}
              dangerouslySetInnerHTML={{ __html: status.message }}
            ></p>
          ) : (
            <p className="text-[14px] font-bold text-[#FF4545] pt-2 mt-4 mb-6">
              매월 1회만 닉네임 변경 가능해요
            </p>
          )}
        </div>
      </div>

      {/* 버튼 영역 */}
      <div className="w-full flex flex-col gap-2">
        <AppButton
          onConfirm={updateNickname}
          className="f-one-pop text-[26px]"
          message="수정하기"
        />
      </div>
    </div>
  );
};
